import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, FileText, Clock, ClipboardList, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";

const ICONS = { sop_update: FileText, reminder: Clock, assignment: ClipboardList };

export const NotificationBell = ({ prefix = "" }) => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const tid = (s) => `${prefix}notification-${s}`;

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative rounded-full border border-lasso-line bg-white p-2.5 transition-colors hover:border-lasso-ink" data-testid={tid("bell")} aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-lasso-ink px-1 text-[10px] font-bold text-white" data-testid={tid("unread-count")}>
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid={tid("panel")}>
        <div className="flex items-center justify-between border-b border-lasso-line px-4 py-3">
          <p className="font-display text-sm font-bold">Notifications</p>
          {data.unread > 0 && (
            <button onClick={readAll} className="inline-flex items-center gap-1 text-xs font-semibold text-lasso-deep hover:text-lasso-ink" data-testid={tid("mark-all-read")}>
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid={tid("empty")}>You're all caught up.</p>}
          {data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => openItem(n)} data-testid={tid(`item-${n.id}`)} className={`flex w-full gap-3 border-b border-lasso-line px-4 py-3 text-left transition-colors last:border-0 hover:bg-lasso-tint ${n.read ? "" : "bg-lasso-paper"}`}>
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.read ? "bg-zinc-100 text-zinc-500" : "bg-lasso-sky text-lasso-ink"}`}><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-start justify-between gap-2">
                    <span className={`text-sm leading-snug ${n.read ? "text-zinc-600" : "font-semibold"}`}>{n.title}</span>
                    {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-lasso-deep" />}
                  </span>
                  {n.body && <span className="mt-1 line-clamp-4 block whitespace-pre-line text-xs leading-relaxed text-zinc-500">{n.body}</span>}
                  <span className="mt-1 block text-[10px] uppercase tracking-[0.14em] text-zinc-400">{formatDate(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
